import React, { useEffect } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetRolesQuery } from '../../store/role/role'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Button } from '../../components/Button/Button'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { getUserName } from '../utils'
import { useCreateExternalUserMutation } from '../../store/externalUser/externalUser'
import { ExternalUserCreateUpdatePayload, User, UserLanguage, UserOwnerType } from '../../models/User'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { useGetAllUsersQuery } from '../../store/user/user'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { LANGUAGE_OPTIONS } from '../../common/languageUtils'

export type AddContactFormValues = {
  firstName: string
  lastName: string
  email: string
  role: string
  referenceUserId: string | null
  ownerId: string
  language: UserLanguage | null
}

export interface AddContactModalProps {
  show: boolean
  onSave: (newExternalUser?: User) => void
  onCancel: () => void
  initialValues?: Partial<AddContactFormValues>
}

export const DEFAULT_CONTACT_ROLE_ID = import.meta.env.REACT_APP_DEFAULT_CONTACT_ROLE_ID!

export const AddContactModal: React.FC<AddContactModalProps> = ({ show, onSave, onCancel, initialValues }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    ownerId: '',
    language: null,
  }

  const [createExternalUser, { data: newExternalUser, isLoading: isExternalUserCreateLoading, isSuccess }] = useCreateExternalUserMutation()

  const getSaveContactPayload = (data: AddContactFormValues): ExternalUserCreateUpdatePayload => {
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      name: getUserName(data.firstName, data.lastName) || undefined,
      email: data.email,
      roleIds: [data.role],
      identity: { type: 'external', location: data.email },
      referenceUserId: data.referenceUserId,
      owner:
        !data.ownerId || data.ownerId === 'null'
          ? null
          : {
              ownerId: data.ownerId,
              ownerType: UserOwnerType.USER,
            },
      settings: {
        language: data.language,
      },
    }
  }

  const handleSave = tracked(
    (data: AddContactFormValues) => {
      createExternalUser({
        accountId,
        userData: getSaveContactPayload(data),
      })
    },
    { eventName: TrackEvent.CONTACT_ADDED },
    {
      eventDetailsExtractor: getSaveContactPayload,
    },
  )

  const form = useForm<AddContactFormValues>({ defaultValues })

  const { handleSubmit, setValue } = form

  useEffect(() => {
    if (!initialValues) {
      return
    }
    const { firstName = '', lastName = '', referenceUserId = null } = initialValues
    setValue('firstName', firstName)
    setValue('lastName', lastName)
    setValue('referenceUserId', referenceUserId)
  }, [initialValues, setValue])

  useEffect(() => {
    if (isSuccess) {
      onSave(newExternalUser?.data)
      form.reset()
    }
  }, [isSuccess])

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Add new contact</h4>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <AddContactForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onCancel}>
        Cancel
      </Button>
      <ScopedBlock scope={{ scope: 'ext_user:create', superScope: ['ext_user:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <Button variant='primary' onClick={handleSubmit(handleSave)} loading={isExternalUserCreateLoading}>
          Save
        </Button>
      </ScopedBlock>
    </div>
  )

  return <Modal show={show} onHide={onCancel} header={<></>} body={body} footer={footer} centered={true} />
}

const AddContactForm: React.FC = () => {
  const { control, setValue } = useFormContext<AddContactFormValues>()
  const accountId = useAppSelector(selectAccountId)
  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, externalOnly: true },
    { refetchOnMountOrArgChange: true },
  )
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })

  const userOptions: SelectOption[] = [
    ...(users?.data.map((user) => {
      return { id: user.id, label: getUserDisplayName(user) }
    }) ?? []),
  ]

  useEffect(() => {
    if (!roleData?.data) {
      return
    }
    const defaultRole = roleData.data.find((r) => r.id === DEFAULT_CONTACT_ROLE_ID)
    if (defaultRole) {
      setValue('role', defaultRole.id)
    }
  }, [roleData, setValue])

  const roleOptions =
    roleData?.data.map((role) => {
      return { id: role.id, label: getRoleDisplayName(role) }
    }) || []

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingRoleData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email address is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address*</Form.Label>
                    <Form.Control id={field.name} type='email' placeholder='Enter email address…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='firstName'
                control={control}
                rules={{
                  required: 'First name is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>First name*</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='lastName'
                control={control}
                rules={{
                  required: 'Last name is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Last name*</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='role'
                control={control}
                rules={{
                  required: 'Role is required.',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Role*</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={roleOptions}
                      initialSelectedOption={roleOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='ownerId'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Owner</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={userOptions}
                      initialSelectedOption={null}
                      onOptionSelected={(option) => {
                        field.onChange(option.id)
                      }}
                      disabled={usersFetching}
                      placeholder='Select Owner'
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='language'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Language</Form.Label>
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={LANGUAGE_OPTIONS}
                      initialSelectedOption={field.value ? LANGUAGE_OPTIONS.find((option) => option.id === field.value) : null}
                      onOptionSelected={(option) => field.onChange(option.id as UserLanguage)}
                      placeholder='Select Language'
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
